import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { fetchCompanyDocuments, fetchCompanyHub, fetchCompanyReadiness } from "./api";
import type { CompanyAcquisitionStatus } from "./types";

export type CompanyDocumentFilters = {
  q: string;
  source: string;
  doc_type: string;
  page: number;
  page_size: number;
};

export const DEFAULT_COMPANY_DOCUMENT_FILTERS: CompanyDocumentFilters = {
  q: "",
  source: "",
  doc_type: "",
  page: 1,
  page_size: 20,
};

const COMPANY_HUB_STALE_TIME_MS = 30_000;
const COMPANY_DOCUMENTS_STALE_TIME_MS = 15_000;
const COMPANY_READINESS_STALE_TIME_MS = 15_000;
export const COMPANY_READINESS_POLL_INTERVAL_MS = 15_000;

export const companyQueryKeys = {
  hubRoot: ["company-hub"] as const,
  fullRoot: ["company"] as const,
  documentsRoot: ["company-documents"] as const,
  readinessRoot: ["company-readiness"] as const,
  hub: (companyRef: string) => ["company-hub", companyRef] as const,
  full: (companyRef: string) => ["company", companyRef] as const,
  documents: (companyRef: string, filters: CompanyDocumentFilters) =>
    ["company-documents", companyRef, filters] as const,
  readiness: (companyRef: string) => ["company-readiness", companyRef] as const,
};

export function companyHubQueryOptions(companyRef: string) {
  return queryOptions({
    queryKey: companyQueryKeys.hub(companyRef),
    queryFn: () => fetchCompanyHub(companyRef),
    staleTime: COMPANY_HUB_STALE_TIME_MS,
  });
}

export function companyDocumentsQueryOptions(
  companyRef: string,
  filters: CompanyDocumentFilters = DEFAULT_COMPANY_DOCUMENT_FILTERS,
) {
  return queryOptions({
    queryKey: companyQueryKeys.documents(companyRef, filters),
    queryFn: ({ signal }) => fetchCompanyDocuments(companyRef, filters, signal),
    staleTime: COMPANY_DOCUMENTS_STALE_TIME_MS,
  });
}

export function companyReadinessPollInterval(
  status: CompanyAcquisitionStatus | undefined,
): number | false {
  return status === "queued" || status === "acquiring"
    ? COMPANY_READINESS_POLL_INTERVAL_MS
    : false;
}

export function companyReadinessQueryOptions(companyRef: string) {
  return queryOptions({
    queryKey: companyQueryKeys.readiness(companyRef),
    queryFn: ({ signal }) => fetchCompanyReadiness(companyRef, signal),
    staleTime: COMPANY_READINESS_STALE_TIME_MS,
    refetchInterval: (query) =>
      companyReadinessPollInterval(query.state.data?.acquisition.status),
  });
}

export async function prefetchCompanyHub(queryClient: QueryClient, companyRef: string): Promise<void> {
  await Promise.all([
    queryClient.prefetchQuery(companyHubQueryOptions(companyRef)),
    queryClient.prefetchQuery(
      companyDocumentsQueryOptions(companyRef, DEFAULT_COMPANY_DOCUMENT_FILTERS),
    ),
  ]);
}

export async function invalidateCompanyDetails(
  queryClient: QueryClient,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: companyQueryKeys.hubRoot }),
    queryClient.invalidateQueries({ queryKey: companyQueryKeys.fullRoot }),
  ]);
}

export async function invalidateCompanyOnboardingQueries(
  queryClient: QueryClient,
  includeReadiness: boolean,
): Promise<void> {
  const options = { refetchType: "none" as const };
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: companyQueryKeys.hubRoot, ...options }),
    queryClient.invalidateQueries({ queryKey: companyQueryKeys.fullRoot, ...options }),
    queryClient.invalidateQueries({ queryKey: companyQueryKeys.documentsRoot, ...options }),
    queryClient.invalidateQueries({ queryKey: ["company-search"], ...options }),
    queryClient.invalidateQueries({ queryKey: ["companies"], ...options }),
    ...(includeReadiness
      ? [queryClient.invalidateQueries({ queryKey: companyQueryKeys.readinessRoot, ...options })]
      : []),
  ]);
}
